import { GetterTree } from 'vuex';
import { RootState } from '@/store';
import { State } from './state';
import { config } from '@/services/config';
import { get, map, find, orderBy } from 'lodash';
import { formatDataValue, formatClientSpecificValues } from '@/helpers/format';
import { FormatOptions, HighchartsSeries } from '@/types/Global';
import {
  GroupedKpiTableData,
  KpiTableAndChart,
  KpiGroup,
  KpiTableData,
  KpiChartItem,
} from '@/types/KPI';
import { ServiceWithGraphKey } from '@/types/Service';
import { getServiceColorClass, getStatusColour } from '@/helpers/colours';
import { createTableHeaders, createTopTableHeaders } from '@/helpers/tables';
import { PortfolioStatus, ServiceOrder } from '@/enums/Global';
import { Header, TopHeader } from '@/types/Table';
import { getFeatureFlag, FeatureFlag } from '@/services/config';

export type Getters = {
  getKpiInfo(
    state: State,
    aggregateFieldType: string
  ): KpiTableAndChart | undefined;
  getKpiDataIsLoading(state: State): boolean;
  getKpiTableData(state: State, aggregateFieldType: string): KpiTableData;
  getBreakdownByService(
    state: State,
    aggregateFieldType: string
  ): Array<HighchartsSeries>;
  getBreakdownByStatus(
    state: State,
    aggregateFieldType: string
  ): Array<HighchartsSeries>;
  getKpiStatus(state: State, aggregateFieldType: string): PortfolioStatus;
  getGroupedKpiTableData(
    state: State,
    aggregateFieldType: string
  ): GroupedKpiTableData;
  getGroupedKpiDataIsLoading(state: State): boolean;
};

export const getters: GetterTree<State, RootState> & Getters = {
  getKpiInfo: (state, aggregateFieldType) => {
    const kpiData = find(state.kpiData, { aggregateFieldType });
    return kpiData;
  },
  getKpiDataIsLoading: state => state.kpiDataIsLoading,
  getKpiTableData: (state, aggregateFieldType) => {
    const kpiData = find(state.kpiData, { aggregateFieldType });
    if (!kpiData || !kpiData.tableData)
      return {
        items: [],
        topHeaders: [],
        headers: [],
        footer: {} as ServiceWithGraphKey,
      };

    const tableConfig = get(config.layout, 'tableLayouts.kpiTable', {});
    const configTopHeaders = get(tableConfig, 'topHeaders', []) as Array<
      TopHeader
    >;
    const configHeaders = get(tableConfig, 'headers', []) as Array<Header>;
    const tableData = kpiData.tableData;
    const footerItemsToExclude = configHeaders.filter(
      header => header.excludeFromFooter
    );

    const items = map(tableData.services, item => {
      return {
        graphKey: formatDataValue(item.kpi),
        kpi: formatDataValue(item.kpi),
        roa: formatDataValue(item.roa, {
          keepZeroAsZero: true,
        } as FormatOptions), // we want roa to show up as 0 instead of a dash in the front end
        aum: formatDataValue(item.aum),
        cash: formatDataValue(item.cash),
        credit: formatDataValue(item.credit),
        revenue: formatDataValue(item.revenue, {
          keepZeroAsZero: true,
        } as FormatOptions), // we want revenue to show up as 0 instead of a dash in the front end
        priceEnforcement: formatDataValue(item.priceEnforcement),
        peExistingPortfolios: formatDataValue(item.peExistingPortfolios),
        peNewPortfolios: formatDataValue(item.peNewPortfolios),
        profitRelative: formatDataValue(item.profitMargin),
        profitAbsolute: formatDataValue(item.profit),
        ...formatClientSpecificValues(item.clientSpecificValues),
      } as ServiceWithGraphKey;
    });

    const footer = {
      kpi: 'grandTotal',
      roa: formatDataValue(tableData.total.roa, {
        keepZeroAsZero: true,
      } as FormatOptions), // we want roa to show up as 0 instead of a dash in the front end
      aum: formatDataValue(tableData.total.aum),
      cash: formatDataValue(tableData.total.cash),
      credit: formatDataValue(tableData.total.credit),
      revenue: formatDataValue(tableData.total.revenue, {
        keepZeroAsZero: true,
      } as FormatOptions), // we want revenue to show up as 0 instead of a dash in the front end
      priceEnforcement: formatDataValue(tableData.total.priceEnforcement),
      peExistingPortfolios: formatDataValue(
        tableData.total.peExistingPortfolios
      ),
      peNewPortfolios: formatDataValue(tableData.total.peNewPortfolios),
      profitRelative: formatDataValue(tableData.total.profitMargin),
      profitAbsolute: formatDataValue(tableData.total.profit),
      ...formatClientSpecificValues(tableData.total.clientSpecificValues),
    } as ServiceWithGraphKey;

    footerItemsToExclude.forEach(item => delete footer[item.key]);

    return {
      items: orderBy(items, x => ServiceOrder[x.kpi as string]),
      topHeaders: createTopTableHeaders(configTopHeaders),
      headers: createTableHeaders(configHeaders),
      footer,
    };
  },
  getGroupedKpiTableData: state => {
    if (!state.groupedKpiData)
      return {
        topHeaders: [],
        headers: [],
        items: [],
      };

    const tableConfig = get(config.layout, 'tableLayouts.groupedKpiTable', {});
    const configTopHeaders = get(tableConfig, 'topHeaders', []);
    const configHeaders = get(tableConfig, 'headers', []);
    const tableData = state.groupedKpiData;

    const createHighChartsSeries = (
      chartItems: Array<KpiChartItem>,
      colourFunction: Function
    ) => {
      return map(chartItems, item => {
        if ((item.value.dataValue as number) <= 0) return null;
        const serviceLevel = item.category.dataValue as string;
        const percentage = item.value.dataValue as number;
        const colourClass = colourFunction(serviceLevel);
        return {
          name: serviceLevel,
          data: [percentage],
          className: `fill-${colourClass} stroke-white`,
          dataType: item.value.dataType,
        };
      }).filter(data => data != null);
    };

    const items = map(tableData, item => {
      const breakdownOfAssets = createHighChartsSeries(
        item.details.breakdownOfAssets,
        getServiceColorClass
      );

      const breakdownByRevenueStatus = createHighChartsSeries(
        item.details.breakdownByRevenueStatus,
        getStatusColour
      );

      const breakdownByProfitStatus = createHighChartsSeries(
        item.details.breakdownByProfitStatus,
        getStatusColour
      );

      const rankingPosition = get(
        item,
        'details.ranking.position.dataValue',
        0
      );
      const rankingTotal = get(item, 'details.ranking.total.dataValue', 0);

      const isFamilyLevelEnabled = getFeatureFlag(
        FeatureFlag.FAMILY_LEVEL_SIMULATED_IMPACT
      ).enabled;

      const itemCount = formatDataValue(item.itemCount);
      const familyCount = isFamilyLevelEnabled ? itemCount : 0;
      const clientCount = !isFamilyLevelEnabled ? itemCount : 0;

      const outsideTargetItemCount = formatDataValue(
        item.outsideTargetItemCount
      );
      const outsideTargetFamilyCount = isFamilyLevelEnabled
        ? outsideTargetItemCount
        : 0;
      const outsideTargetClientCount = !isFamilyLevelEnabled
        ? outsideTargetItemCount
        : 0;

      return {
        name: item.entityId,
        ranking: `${rankingPosition} / ${rankingTotal}`,
        chartData: {
          breakdownOfAssets,
          breakdownByRevenueStatus,
          breakdownByProfitStatus,
        },
        totalsTotalAssets: formatDataValue(item.details.totals.aum),
        clientCount: clientCount,
        familyCount: familyCount,
        outsideTargetTotalAssetsUnderManagement: formatDataValue(
          item.outsideTargetTotalAssetsUnderManagement
        ),
        outsideTargetFamilyCount: outsideTargetFamilyCount,
        outsideTargetClientCount: outsideTargetClientCount,
        tasksCompleted: formatDataValue(item.tasksCompleted),
        tasksReviewing: formatDataValue(item.tasksReviewing),
        tasksReviewingTotalAum: formatDataValue(item.tasksReviewingTotalAum, {
          numberDecimalMaxPoints: 0,
        } as FormatOptions),
      } as KpiGroup;
    });

    return {
      topHeaders: createTopTableHeaders(configTopHeaders),
      headers: createTableHeaders(configHeaders),
      items,
    };
  },
  getGroupedKpiDataIsLoading: state => state.groupedKpiDataIsLoading,
  getBreakdownByService: (state, aggregateFieldType) => {
    const kpiData = find(state.kpiData, { aggregateFieldType });

    if (!kpiData || !kpiData.chartData || !kpiData.chartData.breakdownByService)
      return [];

    return map(kpiData.chartData.breakdownByService, kpiItem => {
      if ((kpiItem.value.dataValue as number) <= 0) return null;
      const serviceLevel = kpiItem.category.dataValue as string;
      const percentage = kpiItem.value.dataValue as number;
      const serviceColorClass = getServiceColorClass(serviceLevel);

      return {
        name: serviceLevel,
        data: [percentage],
        className: `fill-${serviceColorClass} stroke-white`,
        dataType: kpiItem.value.dataType,
      };
    }).filter(data => data !== null) as Array<HighchartsSeries>;
  },
  getBreakdownByStatus: (state, aggregateFieldType) => {
    const kpiData = find(state.kpiData, { aggregateFieldType });

    if (
      !kpiData ||
      !kpiData.chartData ||
      !kpiData.chartData.breakdownByProfitStatus
    )
      return [];

    return map(kpiData.chartData.breakdownByProfitStatus, kpiItem => {
      if ((kpiItem.value.dataValue as number) <= 0) return null;
      const status = kpiItem.category.dataValue as string;
      const percentage = kpiItem.value.dataValue as number;
      const serviceColorClass = getStatusColour(status);

      return {
        name: status,
        data: [percentage],
        className: `fill-${serviceColorClass} stroke-white`,
        dataType: kpiItem.value.dataType,
      };
    }).filter(data => data !== null) as Array<HighchartsSeries>;
  },
  getKpiStatus: (state, aggregateFieldType) => {
    const kpiInformation = find(state.kpiData, { aggregateFieldType });

    if (
      !kpiInformation ||
      !kpiInformation.chartData ||
      !kpiInformation.chartData.status
    )
      return PortfolioStatus.UNKNOWN;

    return kpiInformation.chartData.status.dataValue as PortfolioStatus;
  },
};
